import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import {
  criticalNamespaces,
  getNamespaceForRoute,
  namespaces,
} from "./i18n-config";

export { criticalNamespaces, getNamespaceForRoute, namespaces };

// SSR hydration: the server serializes the translations it rendered with into
// `window.__I18N__`. Reading them here lets the client init i18next
// SYNCHRONOUSLY with the same resources, so `isInitialized`/`ready` are true at
// hydration time. Without this the client mounts before the async HTTP backend
// has loaded anything, renders a different tree (the I18nProvider loader), and
// React throws a hydration mismatch.
declare global {
  interface Window {
    __I18N__?: {
      lng: string;
      store: Record<string, Record<string, Record<string, unknown>>>;
    };
  }
}

const ssrI18n = typeof window !== "undefined" ? window.__I18N__ : undefined;

// SSR serializes the route namespace alongside the critical shell resources.
// Keep those namespaces registered with i18next so a client-side language
// switch asks the bundled backend for the matching locale instead of treating
// the seeded route as an unknown namespace. Namespaces that were not seeded
// remain lazy.
const initialNamespaces = [
  ...new Set([
    ...criticalNamespaces,
    ...Object.keys(ssrI18n?.store[ssrI18n.lng] ?? {}),
  ]),
];

// Vite bundles every locale JSON into the module graph as content-hashed,
// code-split chunks (import.meta.glob). i18next-resources-to-backend loads a
// namespace on demand from those chunks — no HTTP backend, and no static
// /locales/*.json that a CDN can serve stale. The `**` glob also covers nested
// namespaces such as `articles/<slug>`.
const localeModules = import.meta.glob("./locales/**/*.json") as Record<
  string,
  () => Promise<{ default: Record<string, unknown> }>
>;
const bundledResourcesBackend = resourcesToBackend(
  (lng: string, ns: string) => {
    const loader = localeModules[`./locales/${lng}/${ns}.json`];
    if (!loader) {
      return Promise.reject(new Error(`Missing bundled locale: ${lng}/${ns}`));
    }
    return loader().then((m) => m.default);
  },
);

// Initialize i18next instance
i18n
  .use(bundledResourcesBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Seed from the server-serialized store so the client is ready synchronously
    // at hydration (resources present => init is sync => no loader => no mismatch).
    ...(ssrI18n?.store ? { resources: ssrI18n.store, lng: ssrI18n.lng } : {}),
    // Let the bundled-resources backend lazy-load namespaces not in the seed.
    partialBundledLanguages: true,
    fallbackLng: "en",
    supportedLngs: ["en", "sv"],
    defaultNS: "common", // Restored as it contains shared translations used across pages
    ns: initialNamespaces,
    load: "currentOnly", // Only load the current language
    preload: ["en", "sv"], // Preload all supported languages
    initImmediate: false, // Don't wait for initialization to complete
    debug: false, // Disable debugging to reduce console noise
    showSupportNotice: false,
    saveMissing: false, // Disable to prevent infinite loops
    missingKeyHandler: (lng, ns, key, fallbackValue) => {
      // Greatly reduce logging noise - only log severe missing key issues
      if (process.env.NODE_ENV === "development" && Math.random() < 0.01) {
        console.warn(`Missing critical translation: [${lng}] ${ns}:${key}`);
      }
    },
    detection: {
      order: ["path", "querystring", "cookie", "localStorage", "navigator"],
      lookupFromPathIndex: 0,
      caches: ["localStorage", "cookie"],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false, // Disable Suspense to prevent loading flickers
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added removed",
      transEmptyNodeValue: "", // Return empty string for empty nodes
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p"],
    },
  });

// Keep <html lang> in step when the visitor switches language client-side.
if (typeof document !== "undefined") {
  i18n.on("languageChanged", (lng) => {
    document.documentElement.lang = lng.startsWith("en") ? "en" : "sv";
  });
}

// Make i18n instance available in window for debugging
if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  (window as unknown as { i18next: typeof i18n }).i18next = i18n;
}

export default i18n;

// Cache buster: 1747924785610
